<script setup lang="ts">
import { computed } from 'vue';
import BillingAmountDisplay from '@/components/BillingAmountDisplay/BillingAmountDisplay.vue';
import { useI18n } from '@/i18n';
import { useBillingUtils } from '@/use/billingUtils';

/**
 * Minimal structural type — accepts both `PlanData` (offering side) and
 * `Subscription['plan']` (subscription side) without committing to either.
 */
interface PriceablePlan {
  currency?: string;
  interval?: string;
  amount?: number | null;
}

const props = defineProps<{ plan: PriceablePlan }>();

const { t } = useI18n();
const { formatPrice } = useBillingUtils();

const interval = computed(() => {
  const i = props.plan.interval?.toLowerCase();
  if (i === 'month' || i === 'year') return i;
  return 'lifetime';
});

const currency = computed(() => (props.plan.currency ?? 'usd').toUpperCase());

// A missing amount (e.g. a plan that has no price for the selected interval) renders
// a dash placeholder so the card keeps its price layout.
const hasAmount = computed(() => props.plan.amount != null);
const isRecurring = computed(() => interval.value === 'month' || interval.value === 'year');

// Yearly plans are priced per year in Stripe; like the Stripe pricing tables, show the
// per-month equivalent as the headline and the full annual total beneath it.
const headlineAmount = computed(() => {
  const amount = props.plan.amount ?? 0;
  return interval.value === 'year' ? amount / 12 : amount;
});

const annualTotalLabel = computed(() =>
  interval.value === 'year' && props.plan.amount
    ? t('price_billed_annually', { total: formatPrice(props.plan.amount, currency.value) })
    : '',
);

// One-time / lifetime prices are billed once — show that instead of a "/ interval".
const billedOnceLabel = computed(() => t('price_billed_once', { currency: currency.value }));
</script>

<template>
  <div class="billing-price-display">
    <div class="billing-price-display__primary">
      <BillingAmountDisplay
        v-if="hasAmount"
        :currency="currency"
        :interval="interval === 'year' ? 'month' : interval"
        :amount="headlineAmount"
      />
      <div v-else class="billing-price-display__placeholder" aria-hidden="true">—</div>
      <div v-if="hasAmount && plan.amount !== 0 && isRecurring" class="interval">{{ t('price_per_month') }}</div>
    </div>
    <div v-if="interval === 'lifetime' && hasAmount && plan.amount !== 0" class="billed-once">
      {{ billedOnceLabel }}
    </div>
    <div v-else-if="annualTotalLabel" class="annually">{{ annualTotalLabel }}</div>
  </div>
</template>

<style lang="scss" scoped>
.billing-price-display {
  text-align: center;
}

.billing-price-display__primary {
  display: inline-flex;
  align-items: flex-end;
  gap: 0.25rem;
}

.interval,
.annually,
.billed-once {
  color: var(--text-color-lightest, #6b7280); // Fallback color
  font-size: var(--font-size-xs, 0.75rem); // Fallback size
  font-family: var(--font-family-ui, ui-sans-serif, system-ui, sans-serif); // Fallback font
  text-transform: uppercase;
  line-height: 1.2;
}

.annually,
.billed-once {
  margin-top: 0.25rem;
}

.billing-price-display__placeholder {
  font-family: var(--font-family-ui-serif, ui-serif, Georgia, serif);
  font-weight: bold;
  font-size: 2.5rem;
  line-height: 1;
  color: var(--text-color-lightest, #6b7280);
}
</style>
